import { Button, Label, Modal, Select, TextInput } from "flowbite-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Category, Subcategory } from "@/sharedTypes";
import { useGetPaginatedCategoriesQuery, useCreateCategoryMutation } from "@/api/ticketApi/category";
import { HiPlus } from "react-icons/hi";
import CategoryFormDialog from "../categoryList/CategoryFormDialog";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";

interface SubcategoryFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Subcategory) => void;
  mode?: "create" | "edit";
  initialData?: Subcategory;
  errorMessage?: string;
}

export default function SubcategoryFormDialog({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialData,
  errorMessage,
}: SubcategoryFormDialogProps) {
  const { t } = useTranslation(["subcategory", "category"]);
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Subcategory>();

  // Fetch categories with a large page size to ensure most/all are available
  const { data: categoriesData } = useGetPaginatedCategoriesQuery({
      pagination: { page: 1, pageSize: 100 }
  });
  const categories = categoriesData?.items || [];
  
  // priorities removed
  const [isCategoryCreateOpen, setIsCategoryCreateOpen] = useState(false);
  const [createCategory] = useCreateCategoryMutation();
  
  const selectedCategoryId = watch("category_id");
  const selectedCategory = categories.find((c: Category) => c.category_id.toString() === selectedCategoryId?.toString());
  
  // Check if initial category is inactive
  const isInitialCategoryInactive = initialData?.category?.is_active === false;
  // If we are in edit mode, and the initial category is inactive, and we haven't changed the selection
  const showInactiveWarning = true

  useEffect(() => {
    if (open) {
      if (initialData) {
        reset({
          subcategory_name: initialData.subcategory_name,
          subcategory_description: initialData.subcategory_description,
          category_id: initialData.category_id,
          // Removed priority_id
        });
      } else {
        reset({
          subcategory_name: "",
          subcategory_description: "",
          category_id: "",
          // Removed priority_id
        } as Subcategory);
      }
    }
  }, [open, initialData, reset]);

  const handleCreateCategory = (data: Category) => {
    createCategory(data)
      .unwrap()
      .then((res: any) => {
        toast.success(t("category:categoryCreated"));
        setIsCategoryCreateOpen(false);
        // Select the new category
        // The API response might conform to different structures, handle carefully
        const newId = res?.category_id || res?.data?.category_id;
        if (newId) {
             setValue("category_id", newId);
        }
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  return (
    <Modal show={open} onClose={onClose} size="lg">
      <Modal.Header>
        {mode === "edit" ? t("subcategory:edit_title") : t("subcategory:create_subcategory")}
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="subcategory_name" value={t("subcategory:subcategory_name")} />
            <TextInput
              id="subcategory_name"
              placeholder={t("subcategory:name_placeholder")}
              {...register("subcategory_name", {
                required: t("subcategory:name_required"),
              })}
              color={errors.subcategory_name ? "failure" : undefined}
              helperText={errors.subcategory_name?.message as string}
            />
          </div>

          <div>
            <Label htmlFor="subcategory_description" value={t("subcategory:description")} />
            <TextInput
              id="subcategory_description"
              placeholder={t("subcategory:description_placeholder")}
              {...register("subcategory_description", {
                required: t("subcategory:description_required"),
              })}
              color={errors.subcategory_description ? "failure" : undefined}
              helperText={errors.subcategory_description?.message as string}
            />
          </div>

          <Controller
            name="category_id"
            control={control}
            rules={{ required: t("subcategory:category_required") }}
            render={({ field }) => (
              <div>
                <div className="mb-2 flex items-center justify-between">
                    <Label htmlFor="category_id" value={t("subcategory:category_label")} />
                    <Button 
                        size="xs" 
                        color="light" 
                        onClick={() => setIsCategoryCreateOpen(true)}
                        title={t("category:createCategory")}
                    >
                        <HiPlus className="h-4 w-4" />
                    </Button>
                </div>
                {showInactiveWarning && (
                     <p className="mt-1 text-sm text-yellow-500">
                         {t("subcategory:inactive_category_warning")}
                     </p>
                )}
                <Select
                  id="category_id"
                  value={field.value?.toString() || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  color={errors.category_id ? "failure" : undefined}
                  helperText={errors.category_id?.message as string}
                >
                  <option value="" disabled>
                    {t("subcategory:select_category")}
                  </option>
                  {categories.map((category: Category) => (
                    <option key={category.category_id} value={category.category_id}>
                      {category.category_name}
                    </option>
                  ))}
                  {/* Ensure the initial category is shown even if not in the list (e.g. inactive) if we are editing */}
                  {mode === "edit" && initialData?.category && !categories.some((c: Category) => c.category_id === initialData.category_id) && (
                      <option value={initialData.category_id}>
                          {initialData.category.category_name}
                      </option>
                  )}
                </Select>
              </div>
            )}
          />

          {errorMessage && (
            <div className="text-sm text-red-500">{errorMessage}</div>
          )}

          <div className="flex justify-end gap-2">
            <Button color="gray" onClick={onClose}>
              {t("subcategory:cancel")}
            </Button>
            <Button type="submit">
              {mode === "edit" ? t("subcategory:save_changes") : t("subcategory:create_subcategory")}
            </Button>
          </div>
        </form>
      </Modal.Body>
      <CategoryFormDialog 
        open={isCategoryCreateOpen}
        onClose={() => setIsCategoryCreateOpen(false)}
        onSubmit={handleCreateCategory}
      />
    </Modal>
  );
}
                
